import { yupResolver } from '@hookform/resolvers/yup';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useCreateCampaign } from '@retail/campaign/data-access';
import { BrandCampaignPostDTO } from '@retail/campaign/types';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { OrgUnitType } from '@retail/mgpris/config-types';
import {
  brandCampaignSchema,
  BrandCampaignSchema,
  CreateBrandCampaignForm,
} from '@retail/campaign/components';
import { CreateCampaignDialog } from './CreateCampaignDialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CreateBrandCampaignContainer = ({ open, onClose }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();

  const queryClient = useQueryClient();
  const { availableBrandsForChosenTenant } = useUserBrands();
  const { orgUnitType } = useSelectedOrgUnit();

  const { mutateAsync: mutateCreateCampaignAsync, isPending: isLoadingCreate } =
    useCreateCampaign();

  const formMethods = useForm<BrandCampaignSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(brandCampaignSchema(t)),
    defaultValues: {
      ...brandCampaignSchema(t).getDefault(),
      campaignType:
        orgUnitType !== OrgUnitType.RETAIL_BRAND
          ? 'LOCAL'
          : brandCampaignSchema(t).getDefault().campaignType,
    },
  });

  const submitCampaign = formMethods.handleSubmit(async (schema: BrandCampaignSchema) => {
    const brandIds = schema.brands?.length
      ? schema.brands
      : availableBrandsForChosenTenant.map((brand) => brand.brandId);

    try {
      const campaignPostDTO: BrandCampaignPostDTO = {
        brandIds,
        campaignName: schema.campaignName,
        description: schema.description,
        validFrom: dayjs(schema.validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(schema.validTo).format('YYYY-MM-DD'),
        purchasePeriodFrom: dayjs(schema.purchasePeriodFrom).format('YYYY-MM-DD'),
        purchasePeriodTo: dayjs(schema.purchasePeriodTo).format('YYYY-MM-DD'),
        campaignType: schema.campaignType,
        mandatory: schema.mandatory ?? false,
      };

      await mutateCreateCampaignAsync({ body: campaignPostDTO });
      toast.success(t('campaign.messages.createCampaign'));

      queryClient.invalidateQueries({
        queryKey: ['campaigns'],
      });

      onClose();
    } catch (error) {
      console.error(error);
      toast.error(t('campaign.error.createCampaign'));
    }
  }, console.error);

  return (
    <FormProvider {...formMethods}>
      <CreateCampaignDialog
        open={open}
        onClose={onClose}
        isLoading={isLoadingCreate}
        onSubmit={submitCampaign}
      >
        <CreateBrandCampaignForm availableBrands={availableBrandsForChosenTenant} />
      </CreateCampaignDialog>
    </FormProvider>
  );
};
